import { useCallback, useEffect, useState } from 'react';
import type { MasterData, MasterEngineerSkill, TalentMastersQuery } from '@/types/enterprise';
import type { components } from '@/lib/api/types/openapi';
import { api } from '@/lib/api/client';

export function useEnterpriseMastersQuery() {
  const [state, setState] = useState<{ data?: TalentMastersQuery; fetching: boolean; error?: Error }>({ fetching: true });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    Promise.all([
      api.get('/api/v1/enterprise/masters/engineer-positions'),
      api.get('/api/v1/enterprise/masters/engineer-skill-languages'),
      api.get('/api/v1/enterprise/masters/engineer-skills'),
    ])
      .then(
        ([engineerPositions, engineerSkillLanguages, engineerSkills]: [
          MasterData[],
          MasterEngineerSkill[],
          MasterEngineerSkill[],
        ]) => {
          if (!canceled) {
            setState({
              data: { engineerPositions, engineerSkillLanguages, engineerSkills },
              fetching: false,
            });
          }
        },
      )
      .catch((error) => {
        if (!canceled) setState({ fetching: false, error: error instanceof Error ? error : new Error('マスタ取得に失敗しました') });
      });
    return () => {
      canceled = true;
    };
  }, [reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

export type EnterpriseSpokenLanguage = components['schemas']['EnterpriseMasterSpokenLanguage'];

export function useEnterpriseSpokenLanguages() {
  const [spokenLanguages, setSpokenLanguages] = useState<EnterpriseSpokenLanguage[]>([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    let canceled = false;
    api
      .get('/api/v1/enterprise/masters/spoken-languages')
      .then((data) => {
        if (!canceled) setSpokenLanguages(data as EnterpriseSpokenLanguage[]);
      })
      .catch(() => {
        if (!canceled) setSpokenLanguages([]);
      })
      .finally(() => {
        if (!canceled) setFetching(false);
      });
    return () => {
      canceled = true;
    };
  }, []);

  return { spokenLanguages, fetching };
}
